import { useCallback, useContext, useEffect, useState } from "react";
import {
  ConfigurationResponseDto,
  UpdateConfigurationDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../utils/api";
import {
  AuthHeadersRequestOptions,
  hasAuthToken,
  useAuthHeaders,
} from "./useAuthHeaders";
import { GoFlyerAdminContext } from "../contextApi/context";
import {
  CandidateSourceId,
  CandidateSourceMetadata,
  CandidateSourceParamMetadata,
  RecommendationEngineEndpoint,
  RecommendationEngineEndpointConfig,
  SignalMetadata,
} from "../types/recommendationEngine";
import {
  createDefaultPipeline,
  normalizeRecommendationEnginePipeline,
  RECOMMENDATION_ENGINE_ENDPOINT_ORDER,
} from "../utils/recommendationEngine/constants";

/** Catalogs already fetched, keyed by endpoint ("all" for the unfiltered catalog). */
const signalCatalogCache = new Map<string, SignalMetadata[]>();
const candidateSourceCatalogCache = new Map<string, CandidateSourceMetadata[]>();

const catalogCacheKey = (endpoint?: RecommendationEngineEndpoint) =>
  endpoint ?? "all";

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const normalizeSignalMetadata = (raw: unknown): SignalMetadata | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const signal = raw as Partial<SignalMetadata> & {
    summary?: unknown;
    howItIsCalculated?: unknown;
    dataUsed?: unknown;
    relatedCandidateSources?: unknown;
    formulaHint?: unknown;
  };
  if (
    typeof signal.id !== "string" ||
    typeof signal.label !== "string" ||
    typeof signal.description !== "string" ||
    typeof signal.group !== "string"
  ) {
    return null;
  }

  return {
    id: signal.id,
    label: signal.label,
    summary: typeof signal.summary === "string" ? signal.summary : undefined,
    description: signal.description,
    howItIsCalculated:
      typeof signal.howItIsCalculated === "string"
        ? signal.howItIsCalculated
        : undefined,
    dataUsed: toStringArray(signal.dataUsed),
    relatedCandidateSources: toStringArray(signal.relatedCandidateSources),
    formulaHint:
      typeof signal.formulaHint === "string" ? signal.formulaHint : undefined,
    group: signal.group as SignalMetadata["group"],
    valueRange: {
      min:
        typeof signal.valueRange?.min === "number" ? signal.valueRange.min : 0,
      max:
        typeof signal.valueRange?.max === "number" || signal.valueRange?.max === null
          ? signal.valueRange.max
          : null,
    },
    isRaw: Boolean(signal.isRaw),
    applicableEndpoints: Array.isArray(signal.applicableEndpoints)
      ? signal.applicableEndpoints.filter(
          (endpoint): endpoint is RecommendationEngineEndpoint =>
            RECOMMENDATION_ENGINE_ENDPOINT_ORDER.includes(endpoint as RecommendationEngineEndpoint),
        )
      : [...RECOMMENDATION_ENGINE_ENDPOINT_ORDER],
  };
};

const normalizeCandidateSourceParam = (
  raw: unknown,
): CandidateSourceParamMetadata | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const param = raw as Partial<CandidateSourceParamMetadata>;
  if (typeof param.name !== "string" || typeof param.description !== "string") {
    return null;
  }
  const type = param.type === "boolean" ? "boolean" : "number";
  return {
    name: param.name,
    type,
    required: Boolean(param.required),
    description: param.description,
    defaultHint:
      typeof param.defaultHint === "string" ? param.defaultHint : undefined,
  };
};

const normalizeCandidateSourceMetadata = (
  raw: unknown,
): CandidateSourceMetadata | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const source = raw as Partial<CandidateSourceMetadata>;
  if (
    typeof source.id !== "string" ||
    typeof source.label !== "string" ||
    typeof source.group !== "string"
  ) {
    return null;
  }
  return {
    id: source.id as CandidateSourceId,
    label: source.label,
    group: source.group,
    summary: typeof source.summary === "string" ? source.summary : "",
    description:
      typeof source.description === "string" ? source.description : "",
    howCandidatesAreFound:
      typeof source.howCandidatesAreFound === "string"
        ? source.howCandidatesAreFound
        : "",
    dataUsed: toStringArray(source.dataUsed),
    requiredContext: toStringArray(source.requiredContext),
    allowedEndpoints: Array.isArray(source.allowedEndpoints)
      ? source.allowedEndpoints.filter(
          (endpoint): endpoint is RecommendationEngineEndpoint =>
            RECOMMENDATION_ENGINE_ENDPOINT_ORDER.includes(
              endpoint as RecommendationEngineEndpoint,
            ),
        )
      : [...RECOMMENDATION_ENGINE_ENDPOINT_ORDER],
    relatedSignals: toStringArray(source.relatedSignals),
    params: Array.isArray(source.params)
      ? source.params
          .map((param) => normalizeCandidateSourceParam(param))
          .filter(
            (param): param is CandidateSourceParamMetadata => param !== null,
          )
      : [],
  };
};

export const fetchRecommendationEngineSignals = async (
  authHeaders: AuthHeadersRequestOptions,
  endpoint?: RecommendationEngineEndpoint,
): Promise<SignalMetadata[]> => {
  const payload =
    await adminApiService.recommendationEngineAdminControllerGetSignals(
      endpoint,
      authHeaders,
    );
  return Array.isArray(payload)
    ? payload
        .map((signal) => normalizeSignalMetadata(signal))
        .filter((signal): signal is SignalMetadata => Boolean(signal))
    : [];
};

export const fetchRecommendationEngineCandidateSources = async (
  authHeaders: AuthHeadersRequestOptions,
  endpoint?: RecommendationEngineEndpoint,
): Promise<CandidateSourceMetadata[]> => {
  const payload =
    await adminApiService.recommendationEngineAdminControllerGetCandidateSources(
      endpoint,
      authHeaders,
    );
  return Array.isArray(payload)
    ? payload
        .map((source) => normalizeCandidateSourceMetadata(source))
        .filter(
          (source): source is CandidateSourceMetadata => source !== null,
        )
    : [];
};

/**
 * The signal and candidate-source catalogs the pipeline editor lists. With an
 * endpoint, only what that endpoint's pipelines may use; without one, every
 * entry.
 */
export const useRecommendationEngineCatalogs = (
  endpoint?: RecommendationEngineEndpoint,
) => {
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();
  const cacheKey = catalogCacheKey(endpoint);
  const [signals, setSignals] = useState<SignalMetadata[]>(
    () => signalCatalogCache.get(cacheKey) ?? [],
  );
  const [candidateSources, setCandidateSources] = useState<
    CandidateSourceMetadata[]
  >(() => candidateSourceCatalogCache.get(cacheKey) ?? []);

  const loadSignals = useCallback(async () => {
    if (!hasAuthToken(state.user)) {
      return;
    }
    const cached = signalCatalogCache.get(cacheKey);
    if (cached) {
      setSignals(cached);
      return;
    }
    try {
      const catalog = await fetchRecommendationEngineSignals(
        authHeaders,
        endpoint,
      );
      const next = Array.isArray(catalog) ? catalog : [];
      signalCatalogCache.set(cacheKey, next);
      setSignals(next);
    } catch (error) {
      console.error("Failed to load recommendation engine signals:", error);
      setSignals([]);
    }
  }, [authHeaders, cacheKey, endpoint, state.user]);

  const loadCandidateSources = useCallback(async () => {
    if (!hasAuthToken(state.user)) {
      return;
    }
    const cached = candidateSourceCatalogCache.get(cacheKey);
    if (cached) {
      setCandidateSources(cached);
      return;
    }
    try {
      const catalog = await fetchRecommendationEngineCandidateSources(
        authHeaders,
        endpoint,
      );
      const next = Array.isArray(catalog) ? catalog : [];
      candidateSourceCatalogCache.set(cacheKey, next);
      setCandidateSources(next);
    } catch (error) {
      console.error(
        "Failed to load recommendation engine candidate sources:",
        error,
      );
      setCandidateSources([]);
    }
  }, [authHeaders, cacheKey, endpoint, state.user]);

  useEffect(() => {
    loadSignals();
    loadCandidateSources();
  }, [loadSignals, loadCandidateSources]);

  return { signals, candidateSources };
};

export const useRecommendationEnginePipelineEditor = (
  configKey: string,
  endpoint: RecommendationEngineEndpoint,
) => {
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();
  const [loadedVersion, setLoadedVersion] = useState(0);
  const [draft, setDraft] = useState<RecommendationEngineEndpointConfig>(
    createDefaultPipeline(endpoint),
  );
  const [dirty, setDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { signals, candidateSources } = useRecommendationEngineCatalogs();
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadPipeline = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    if (!hasAuthToken(state.user)) {
      setLoadError(
        "Not authenticated. Sign in again to load recommendation engine settings.",
      );
      setIsLoading(false);
      return;
    }
    try {
      const response: ConfigurationResponseDto =
        await adminApiService.gfMiscellaneousAdminControllerGetConfigurationByKey(
          configKey,
          authHeaders,
        );
      const pipeline = normalizeRecommendationEnginePipeline(response.value, endpoint);
      setDraft(pipeline);
      setLoadedVersion(pipeline.version);
      setDirty(false);
    } catch (error) {
      console.error("Failed to load recommendation engine pipeline:", error);
      setDraft(createDefaultPipeline(endpoint));
      setLoadedVersion(0);
      setLoadError("Failed to load recommendation engine configuration.");
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, configKey, endpoint, state.user]);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const updateDraft = useCallback(
    (
      updater: (prev: RecommendationEngineEndpointConfig) => RecommendationEngineEndpointConfig,
    ) => {
      setDraft((prev) => {
        const next = updater(prev);
        setDirty(true);
        return next;
      });
    },
    [],
  );

  const savePipeline = async (reason?: string) => {
    setIsSaving(true);
    try {
      const payload: UpdateConfigurationDto = {
        value: { ...draft, version: loadedVersion } as unknown as {
          [key: string]: unknown;
        },
        expectedVersion: loadedVersion,
        reason,
      };
      const response =
        await adminApiService.gfMiscellaneousAdminControllerUpdateConfiguration(
          payload,
          configKey,
          authHeaders,
        );
      const savedRaw =
        response.data?.newValue ?? response.data?.value ?? response.data;
      const newPipeline = normalizeRecommendationEnginePipeline(savedRaw ?? draft, endpoint);
      setDraft(newPipeline);
      setLoadedVersion(newPipeline.version);
      setDirty(false);
      return { success: true as const };
    } catch (error: unknown) {
      const err = error as {
        json?: () => Promise<{ message?: string; errors?: string[] }>;
      };
      if (typeof err?.json === "function") {
        const body = await err.json();
        return {
          success: false as const,
          message: body.message,
          errors: body.errors,
        };
      }
      return {
        success: false as const,
        message: "Failed to save recommendation engine configuration.",
      };
    } finally {
      setIsSaving(false);
    }
  };

  const resetPipeline = async () => {
    setIsSaving(true);
    try {
      await adminApiService.gfMiscellaneousAdminControllerResetConfiguration(
        configKey,
        authHeaders,
      );
      await loadPipeline();
      return { success: true as const };
    } catch (error) {
      console.error("Failed to reset recommendation engine pipeline:", error);
      return {
        success: false as const,
        message: "Failed to reset the recommendation engine configuration to the server default.",
      };
    } finally {
      setIsSaving(false);
    }
  };

  const discardChanges = () => {
    loadPipeline();
  };

  return {
    loadedVersion,
    draft,
    dirty,
    isLoading,
    isSaving,
    signals,
    candidateSources,
    loadError,
    updateDraft,
    setDraft,
    setDirty,
    savePipeline,
    resetPipeline,
    discardChanges,
    reload: loadPipeline,
  };
};
